import { streamTextLines } from "@/routes/environments/log-streams/log-groups";
import { streamWithCache } from "@/utils/stream-cache";
import { getConversationCacheKey } from "@/utils/dashboard-conversation-stream";
import type { JsonValue, PlainMessage } from "@bufbuild/protobuf";
import {
    AgentResponseBlock,
    AgentResponseBlock_Phase,
    AgentResponseBlock_TextOutput_Type,
    AgentResponseBlock_TodoItem_Phase,
    AgentResponseBlock_FileModification_DiffGroup_Type,
    UserInputBlock,
    UserInputBlock_TextInput,
    type AgentResponseBlock_ActionCompleted,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCreateAgentExecutionConversationToken } from "@/queries/agent-queries";

const ERROR_DISPLAY_THRESHOLD = 3;

export type ConversationBlock =
    | {
          id: string;
          type: "user";
          content: PlainMessage<UserInputBlock>;
      }
    | {
          id: string;
          type: "agent";
          content: PlainMessage<AgentResponseBlock>;
      };

export type Message = AgentMessage | UserMessage;

/**
 * Represents a message that has been submitted, but hasn't yet been recorded in the conversion.
 */
export type PendingMessage = {
    // This is a client-defined ID which the backend will use for the block ID in the conversation
    id: string;
    content: string;
};

/**
 * Type guard to check if a Message is an AgentResponse
 */
function isAgentResponse(message?: Message): message is AgentMessage {
    return message?.type === "agent";
}

/**
 * Type guard to check if a Message is a UserMessage
 */
function isUserMessage(message?: Message): message is UserMessage {
    return message?.type === "user";
}

/**
 * A UserMessage can include multiple user inputs
 * It represents a full "turn" in the conversation.
 */
export type UserMessage = {
    id: string;
    type: "user";
    fragments: UserMessageFragment[];
};

/**
 * This is similar to UserInputBlock_<XYZ>Input
 */
type UserMessageFragment = {
    type: "text";
    id: string;
    content: string;
};

/**
 * An AgentMessage can include multiple agent response such as text, tool use, etc.
 * It represents a full "turn" in the conversation.
 */
export type AgentMessage = {
    id: number;
    type: "agent";
    fragments: AgentMessageFragment[];
};

/**
 * This is similar to AgentResponseBlock_<XYZ>Output, but where actionStarted and actionCompleted are merged into a single type
 * as that simplifies the UI logic.
 */
export type AgentMessageFragment =
    | AgentMessageFragmentText
    | AgentMessageFragmentAction
    | AgentMessageFragmentFileModification
    | AgentMessageFragmentEnvironmentCreation
    | AgentMessageFragmentThinking
    | AgentMessageFragmentHostAuthenticationRequired
    | AgentMessageFragmentTodoItem
    | AgentMessageFragmentUserInputSeen
    | AgentMessageFragmentCodeAnnotation;

export type AgentMessageFragmentType = AgentMessageFragment["type"];

export type AgentMessageFragmentText = {
    type: "text";
    id: string;
    content: string;
    completed: boolean;
};

export type AgentMessageFragmentThinking = {
    type: "thinking";
    id: string;
    content: string;
    completed: boolean;
};

export type AgentMessageFragmentFileModification = {
    type: "fileModification";
    id: string;
    actionId: string;
    filePath: string;
    content: string;
    // Enhanced diff format from the agent
    diff?: {
        diffGroups: {
            type: "added" | "removed" | "unchanged";
            lines: string[];
        }[];
        stats: {
            additions: number;
            deletions: number;
        };
    };
};

export type AgentMessageFragmentEnvironmentCreation = {
    type: "environmentCreation";
    id: string;
    actionId: string;
    environmentId: string;
};

export type AgentMessageFragmentAction = {
    type: "action";
    id: string;
    actionId: string;
    state: "running" | "completed" | "failed";
    intentDescription: string;
    resultDescription: string;
};

export type AgentMessageFragmentHostAuthenticationRequired = {
    type: "hostAuthenticationRequired";
    id: string;
    actionId: string;
    host: string;
    runnerId: string;
};

export type AgentMessageFragmentTodoItem = {
    type: "todoItem";
    id: string;
    todoItemId: string;
    title: string;
    status: AgentResponseBlock_TodoItem_Phase;
};

export type AgentMessageFragmentUserInputSeen = {
    type: "userInputSeen";
    id: string;
    value: {
        id: string;
    };
};

export type AgentMessageFragmentCodeAnnotation = {
    type: "codeAnnotation";
    id: string;
    filePath: string;
    startLine: number;
    endLine: number;
    description: string;
    title: string;
};

export type LastBlockActivityContext = {
    id?: string;
    status?: string;
    timestamp: number;
};

/**
 * Type guard to check if a fragment is an AgentResponseFragmentAction
 */
function isAgentResponseFragmentAction(fragment: AgentMessageFragment): fragment is AgentMessageFragmentAction {
    return fragment.type === "action";
}

/**
 * Converts a list of ConversationBlocks into a list of Messages suitable for rendering.
 * The order of the blocks is important, as it influces how the blocks are converted into messages.
 */
export function convertToMessages(blocks: ConversationBlock[]): Message[] {
    const messages: Message[] = [];
    const seenUserInputIds = new Set<string>();

    if (blocks.length === 0) {
        return messages;
    }

    let currentMessage: Message | undefined;
    for (const block of blocks) {
        if (block.type === "user" && block.content.id) {
            if (seenUserInputIds.has(block.content.id)) {
                console.warn("Duplicate user input block filtered in UI", {
                    blockId: block.id,
                    userInputId: block.content.id,
                    detectionPoint: "ui_rendering",
                });
                continue;
            }
            seenUserInputIds.add(block.content.id);
        }
        // If the block type is different from the current message type, we need to create a new message
        // as it indicates the "turn" has changed.
        if (currentMessage && block.type !== currentMessage?.type) {
            messages.push(currentMessage);
            currentMessage = undefined;
        }

        if (block.type === "user") {
            if (!currentMessage) {
                currentMessage = {
                    type: "user",
                    id: `message-${messages.length + 1}`,
                    fragments: [],
                };
            }

            if (isUserMessage(currentMessage)) {
                switch (block.content.input.case) {
                    case "text":
                        currentMessage.fragments.push({
                            type: "text",
                            id: block.id,
                            content: block.content.input.value.content,
                        });
                        break;
                }
            }
        }

        if (block.type === "agent") {
            if (!currentMessage) {
                currentMessage = {
                    type: "agent",
                    id: messages.length + 1,
                    fragments: [],
                };
            }

            if (isAgentResponse(currentMessage)) {
                switch (block.content.output.case) {
                    case "userInputSeen":
                        currentMessage.fragments.push({
                            type: "userInputSeen",
                            id: block.id,
                            value: {
                                id: block.content.output.value.id,
                            },
                        });
                        break;
                    case "text":
                        if (block.content.output.value.type === AgentResponseBlock_TextOutput_Type.THOUGHTS) {
                            currentMessage.fragments.push({
                                type: "thinking",
                                id: block.id,
                                content: block.content.output.value.content,
                                completed: block.content.phase === AgentResponseBlock_Phase.COMPLETED,
                            });
                        } else {
                            currentMessage.fragments.push({
                                type: "text",
                                id: block.id,
                                content: block.content.output.value.content,
                                completed: block.content.phase === AgentResponseBlock_Phase.COMPLETED,
                            });
                        }
                        break;
                    case "actionStarted":
                        currentMessage.fragments.push({
                            type: "action",
                            id: block.id,
                            actionId: block.content.output.value.actionId,
                            state: "running",
                            intentDescription: block.content.output.value.intentDescription,
                            resultDescription: "",
                        });
                        break;
                    case "actionCompleted":
                        markActionCompleted(currentMessage, block.content.output.value);
                        break;
                    case "fileModification": {
                        const fileModValue = block.content.output.value;
                        const fragment: AgentMessageFragmentFileModification = {
                            type: "fileModification",
                            id: block.id,
                            actionId: fileModValue.actionId,
                            filePath: fileModValue.filePath,
                            content: fileModValue.content,
                        };

                        // Include diff if available
                        if (fileModValue.diff) {
                            fragment.diff = {
                                diffGroups: fileModValue.diff.diffGroups.map((group) => ({
                                    type:
                                        group.type === AgentResponseBlock_FileModification_DiffGroup_Type.ADDED
                                            ? ("added" as const)
                                            : group.type === AgentResponseBlock_FileModification_DiffGroup_Type.REMOVED
                                              ? ("removed" as const)
                                              : ("unchanged" as const),
                                    lines: [...group.lines],
                                })),
                                stats: {
                                    additions: fileModValue.diff.stats?.additions ?? 0,
                                    deletions: fileModValue.diff.stats?.deletions ?? 0,
                                },
                            };
                        }

                        currentMessage.fragments.push(fragment);
                        break;
                    }
                    case "environmentCreation":
                        currentMessage.fragments.push({
                            type: "environmentCreation",
                            id: block.id,
                            actionId: block.content.output.value.actionId,
                            environmentId: block.content.output.value.environmentId,
                        });
                        break;
                    case "hostAuthenticationRequired":
                        currentMessage.fragments.push({
                            type: "hostAuthenticationRequired",
                            id: block.id,
                            actionId: block.content.output.value.actionId,
                            host: block.content.output.value.host,
                            runnerId: block.content.output.value.runnerId,
                        });
                        break;
                    case "todoItem": {
                        currentMessage.fragments.push({
                            type: "todoItem",
                            id: block.id,
                            todoItemId: block.content.output.value.id,
                            title: block.content.output.value.title,
                            status: block.content.output.value.phase,
                        });
                        break;
                    }
                    case "codeAnnotation":
                        currentMessage.fragments.push({
                            type: "codeAnnotation",
                            id: block.id,
                            filePath: block.content.output.value.filePath,
                            startLine: block.content.output.value.startLine,
                            endLine: block.content.output.value.endLine,
                            description: block.content.output.value.description,
                            title: block.content.output.value.title || "",
                        });
                        break;
                }
            }
        }
    }

    if (currentMessage) {
        messages.push(currentMessage);
    }

    return messages;
}

function markActionCompleted(currentMessage: AgentMessage, block: PlainMessage<AgentResponseBlock_ActionCompleted>) {
    const action: AgentMessageFragmentAction | undefined = currentMessage.fragments.find(
        (f): f is AgentMessageFragmentAction => isAgentResponseFragmentAction(f) && f.actionId === block.actionId,
    );
    if (action) {
        action.state = block.failed ? "failed" : "completed";
        action.resultDescription = block.resultDescription;
    }
}

export async function* parseNDJSON(
    stream: ReadableStream<Uint8Array>,
    signal: AbortSignal,
): AsyncGenerator<ConversationBlock[], void, void> {
    let index = 0;
    for await (const lines of streamTextLines(stream, signal)) {
        const blocks: ConversationBlock[] = [];
        for (const line of lines) {
            const block = parseConversationBlock(index++, line);
            if (block) {
                blocks.push(block);
            }
        }
        yield blocks;
    }
}

export function parseConversationBlock(index: number, line: string): ConversationBlock | null {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const block = JSON.parse(line);
        if (block.id && "phase" in block) {
            if ("userInput" in block) {
                return {
                    id: `input-${block.id}`,
                    type: "user",
                    content: UserInputBlock.fromJson(block.userInput as JsonValue),
                };
            }
            return {
                id: `output-${block.id}`,
                type: "agent",
                content: AgentResponseBlock.fromJson(block as JsonValue),
            };
        } else if (block.actionStarted?.actionId) {
            // TODO(NEXT-3147): ↑ is a temporary workaround until we've fixed the underlying issue
            return {
                id: `output-${block.actionStarted.actionId}`,
                type: "agent",
                content: AgentResponseBlock.fromJson(block as JsonValue),
            };
        } else {
            const id = String("id" in block ? block.id : index);
            return {
                id: `input-${id}`,
                type: "user",
                content: UserInputBlock.fromJson(block as JsonValue),
            };
        }
    } catch (e) {
        console.warn("Failed to parse block:", e, line);
        return null;
    }
}

/**
 * Hook to stream conversation data from a URL
 */
export function useStreamConversation(
    agentExecutionId?: string,
    url?: string,
    pendingMessages?: PendingMessage[],
): {
    error?: Error;
    isLoading: boolean;
    loadingMessage?: string;
    messages: Message[];
    todoItems: AgentMessageFragmentTodoItem[];
    lastAgentBlockContext?: LastBlockActivityContext;
} {
    const [error, setError] = useState<Error | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState<string | undefined>();
    const [blocks, setBlocks] = useState<ConversationBlock[]>([]);
    const [, setFailureCount] = useState(0);
    const [lastAgentBlockContext, setLastAgentBlockContext] = useState<LastBlockActivityContext | undefined>(undefined);
    const { data: token, error: tokenError } = useCreateAgentExecutionConversationToken(agentExecutionId);

    // Check for URL override in localStorage (development only)
    const overrideUrl = agentExecutionId ? localStorage.getItem(`conversation-url-override-${agentExecutionId}`) : null;
    const effectiveUrl = overrideUrl || url;

    const waitAndRenderRetry = useCallback(async (seconds: number, signal: AbortSignal) => {
        for (let i = seconds; i >= 0 && !signal.aborted; i--) {
            setLoadingMessage(i === 0 ? "Retrying now..." : `Retrying in ${i}s...`);
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }
    }, []);

    const streamFromUrl = useCallback(
        async (signal: AbortSignal) => {
            if (!effectiveUrl || !token || !agentExecutionId) return;

            let error: Error | undefined;

            while (!signal.aborted) {
                try {
                    // Reset state when starting a new fetch
                    setIsLoading(true);
                    setBlocks([]);
                    setLoadingMessage(undefined);
                    setLastAgentBlockContext(undefined);

                    const cacheKey = getConversationCacheKey(agentExecutionId);
                    const stream = streamWithCache(cacheKey, effectiveUrl, {
                        signal,
                        mode: "cors",
                        keepalive: false,
                        headers: { Authorization: `Bearer ${token}` },
                    });

                    for await (const item of parseNDJSON(stream, signal)) {
                        setBlocks((prevBlocks) => [...prevBlocks, ...item]);

                        // Update last agent activity context to we can track if the conversation is stuck.
                        const agentBlocks = item.filter((block) => block.type === "agent");
                        if (agentBlocks.length > 0) {
                            const agentBlockContext: LastBlockActivityContext = {
                                timestamp: Date.now(),
                            };

                            const lastAgentBlock = agentBlocks[agentBlocks.length - 1];
                            if (lastAgentBlock.type === "agent") {
                                agentBlockContext.status = lastAgentBlock.content.output.case;
                                agentBlockContext.id = lastAgentBlock.content.id;
                            }

                            setLastAgentBlockContext(agentBlockContext);
                        }

                        // If we had previously encountered an error, we reset it once
                        // we've successfully parsed a new block.
                        if (error !== undefined) {
                            error = undefined;
                            setError(undefined);
                            setFailureCount(0); // Reset failure count on successful connection
                        }
                    }

                    throw new Error("Stream closed unexpectedly");
                } catch (err) {
                    if (signal.aborted) return;

                    error = err instanceof Error ? err : new Error(String(err));
                    setFailureCount((prev) => {
                        const newCount = prev + 1;
                        // Show error after 3 failed attempts (1-based: attempts 1, 2, 3)
                        if (newCount >= ERROR_DISPLAY_THRESHOLD) {
                            setError(error);
                        }
                        return newCount;
                    });

                    // retry after 3s
                    await waitAndRenderRetry(3, signal);
                } finally {
                    if (!signal.aborted) {
                        setIsLoading(false);
                    }
                }
            }
        },
        [effectiveUrl, token, agentExecutionId, waitAndRenderRetry],
    );

    useEffect(() => {
        const abortController = new AbortController();
        if (effectiveUrl) {
            void streamFromUrl(abortController.signal);
        }

        return () => {
            abortController.abort();
        };
    }, [effectiveUrl, streamFromUrl]);

    const { messages, todoItems } = useMemo(() => {
        const { messages } = processBlocks(blocks, pendingMessages);
        const todoItems = extractTodoItems(messages);
        return { messages, todoItems };
    }, [blocks, pendingMessages]);

    if (!agentExecutionId) {
        return {
            error: undefined,
            isLoading: false,
            loadingMessage: undefined,
            messages: [],
            todoItems: [],
            lastAgentBlockContext: undefined,
        };
    }

    return {
        error: error ?? (tokenError || undefined),
        isLoading,
        loadingMessage,
        messages,
        todoItems,
        lastAgentBlockContext,
    };
}

export function processBlocks(
    blocks: ConversationBlock[],
    pendingMessages?: PendingMessage[],
): {
    messages: Message[];
} {
    const length = blocks.length;
    if (!length) {
        return { messages: [] };
    }

    // make responses unique based on their id, choosing the last entry in the list
    // Using a map + array to keep the order of the blocks
    const idToBlockMap = new Map<string, ConversationBlock>();
    const orderedIds: string[] = [];

    for (let i = 0; i < blocks.length; i++) {
        const block = blocks[i];
        const previous = idToBlockMap.get(block.id);
        if (!previous) {
            orderedIds.push(block.id);
            idToBlockMap.set(block.id, block);
        } else {
            const merged = mergeBlock(previous, block);
            idToBlockMap.set(block.id, merged);
        }

        // Look at the previous block in the list and if it is from an agent, of type text,
        // and not marked as completed, mark it as completed to handle cases where the
        // agent execution didn't mark it as completed itself - happens during interruptions
        if (i > 0) {
            const previousBlock = blocks[i - 1];
            if (
                previousBlock &&
                previousBlock.id !== block.id && // Don't mark the current block as completed
                previousBlock.type === "agent" &&
                previousBlock.content.output.case === "text" &&
                previousBlock.content.phase !== AgentResponseBlock_Phase.COMPLETED
            ) {
                const previousBlockInMap = idToBlockMap.get(previousBlock.id);
                if (previousBlockInMap && previousBlockInMap.type === "agent") {
                    const updatedBlock: ConversationBlock = {
                        ...previousBlockInMap,
                        content: {
                            ...previousBlockInMap.content,
                            phase: AgentResponseBlock_Phase.COMPLETED,
                        },
                    };
                    idToBlockMap.set(previousBlock.id, updatedBlock);
                }
            }
        }
    }

    // More efficient array construction without filter
    const res: ConversationBlock[] = [];
    for (const id of orderedIds) {
        const block = idToBlockMap.get(id);
        if (block) {
            res.push(block);
        }
    }

    // For any pending message that isn't already in the stream we add them to the end of the stream.
    // This acts as a sort of optimistic update, where it will appear like the pending messages are already in the stream.
    for (const pendingMessage of pendingMessages ?? []) {
        // We only want to add the pending messages that are not yet present in the conversation already
        const seenInStream = idToBlockMap.has(`input-${pendingMessage.id}`);
        if (!seenInStream) {
            res.push({
                id: pendingMessage.id,
                type: "user",
                content: new UserInputBlock({
                    input: {
                        case: "text",
                        value: new UserInputBlock_TextInput({ content: pendingMessage.content }),
                    },
                }),
            });
        }
    }

    // Convert blocks to messages first
    const messages = convertToMessages(res);

    // Process todo list summaries
    const messagesWithSummaries = injectTodoListSummaries(messages);

    return { messages: messagesWithSummaries };
}

function processTodoItemFragment(
    fragment: AgentMessageFragmentTodoItem,
    todoItemsMap: Map<string, AgentMessageFragmentTodoItem>,
    currentTodoSet: Set<string>,
): { isNew: boolean; isCleared: boolean } {
    const existingItem = todoItemsMap.get(fragment.todoItemId);
    let isNew = false;
    let isCleared = false;

    if (fragment.status === AgentResponseBlock_TodoItem_Phase.CLEARED) {
        isCleared = true;
        currentTodoSet.delete(fragment.todoItemId);

        // Preserve the original title and status when clearing
        if (existingItem) {
            todoItemsMap.set(fragment.todoItemId, {
                ...fragment,
                title: existingItem.title,
                status: existingItem.status,
            });
        } else {
            todoItemsMap.set(fragment.todoItemId, fragment);
        }
    } else {
        if (existingItem) {
            // Update existing item
            todoItemsMap.set(fragment.todoItemId, fragment);
            currentTodoSet.add(fragment.todoItemId);
        } else {
            // First time seeing this todo item
            todoItemsMap.set(fragment.todoItemId, fragment);
            currentTodoSet.add(fragment.todoItemId);
            isNew = true;
        }
    }

    return { isNew, isCleared };
}

type TodoState = {
    todoItemsMap: Map<string, AgentMessageFragmentTodoItem>;
    currentTodoSet: Set<string>;
};

function processTodoFragments(
    fragments: AgentMessageFragment[],
    todoState: TodoState,
): { clearedTodoIds: string[]; clearedActionIndex: number } {
    const clearedTodoIds: string[] = [];
    let clearedActionIndex = -1;
    let foundClearAction = false;

    for (let i = 0; i < fragments.length; i++) {
        const fragment = fragments[i];

        // Check for clear action only if we haven't found one yet
        if (!foundClearAction && fragment.type === "action" && fragment.intentDescription.startsWith("Clearing ")) {
            if (/^Clearing (\d+ todo items?|todo list)$/.test(fragment.intentDescription)) {
                clearedActionIndex = i;
                foundClearAction = true;
            }
        } else if (fragment.type === "todoItem") {
            const { isCleared } = processTodoItemFragment(fragment, todoState.todoItemsMap, todoState.currentTodoSet);
            if (isCleared) {
                clearedTodoIds.push(fragment.todoItemId);
            }
        }
    }

    return { clearedTodoIds, clearedActionIndex };
}

// Get cleared todos in their original creation order, not clearing order
function getClearedTodos(
    todoItemsMap: Map<string, AgentMessageFragmentTodoItem>,
    clearedIds: string[],
): AgentMessageFragmentTodoItem[] {
    const clearedTodos: AgentMessageFragmentTodoItem[] = [];
    const clearedIdSet = new Set(clearedIds);

    // Iterate through todos in their original insertion order (Map preserves insertion order)
    for (const todo of todoItemsMap.values()) {
        if (clearedIdSet.has(todo.todoItemId)) {
            clearedTodos.push(todo);
        }
    }
    return clearedTodos;
}

function resetTodoTracking(todoState: TodoState): void {
    todoState.currentTodoSet.clear();

    for (const todo of todoState.todoItemsMap.values()) {
        if (todo.status !== AgentResponseBlock_TodoItem_Phase.CLEARED) {
            todoState.currentTodoSet.add(todo.todoItemId);
        }
    }
}

function injectTodoListSummaries(messages: Message[]): Message[] {
    const result: Message[] = [];
    const todoState: TodoState = {
        todoItemsMap: new Map(),
        currentTodoSet: new Set(),
    };

    for (const message of messages) {
        if (message.type === "agent") {
            // Batch process all todo-related fragments in a single pass
            const { clearedTodoIds, clearedActionIndex } = processTodoFragments(message.fragments, todoState);

            // Handle todo clearing if any todos were cleared
            if (clearedTodoIds.length > 0) {
                const clearedTodos = getClearedTodos(todoState.todoItemsMap, clearedTodoIds);

                if (clearedTodos.length > 0 && clearedActionIndex >= 0) {
                    // Skip creating summary fragment - just use original fragments
                    result.push({
                        id: message.id,
                        type: "agent",
                        fragments: message.fragments,
                    });
                } else {
                    result.push(message);
                }

                resetTodoTracking(todoState);
            } else {
                result.push(message);
            }
        } else {
            result.push(message);
        }
    }

    return result;
}

// Extract todo items with proper cleared status handling for TodoList filtering
export function extractTodoItems(messages: Message[]): AgentMessageFragmentTodoItem[] {
    const todoItemsMap = new Map<string, AgentMessageFragmentTodoItem>();

    // Single pass through all messages and fragments
    for (const message of messages) {
        if (message.type === "agent") {
            for (const fragment of message.fragments) {
                if (fragment.type === "todoItem") {
                    const existingItem = todoItemsMap.get(fragment.todoItemId);

                    if (existingItem) {
                        // If the new fragment is CLEARED, preserve the title but keep CLEARED status
                        if (fragment.status === AgentResponseBlock_TodoItem_Phase.CLEARED) {
                            todoItemsMap.set(fragment.todoItemId, {
                                ...fragment,
                                title: existingItem.title, // Preserve the original title
                            });
                        } else {
                            // For other phases, use the new fragment (which may have an updated title)
                            todoItemsMap.set(fragment.todoItemId, fragment);
                        }
                    } else {
                        // First time seeing this todo item
                        todoItemsMap.set(fragment.todoItemId, fragment);
                    }
                }
            }
        }
    }

    // Convert map values to array more efficiently
    return [...todoItemsMap.values()];
}

/*
 * This merges agent blocks of type text to handle delta updates.
 * I want to do something different in the near future, but for now this makes the rest of the UI compatible
 * with the pre-delta agent stream.
 */
function mergeBlock(previous: ConversationBlock, current: ConversationBlock): ConversationBlock {
    // Should never happen, but you never know.
    if (previous.type !== current.type) {
        throw new Error("Cannot merge blocks of different types");
    }

    // No special merging for user blocks
    if (current.type === "user") {
        return current;
    }

    // For some very unfortunate reason, we produced UPDATE blocks using the delta semantics
    // so we need to treat UPDATE blocks as DELTA blocks
    const isDelta =
        current.content.phase === AgentResponseBlock_Phase.UPDATE ||
        current.content.phase === AgentResponseBlock_Phase.DELTA;

    // We only care about merging text blocks
    if (
        // We know this from the check above, but the TS compiler does not.
        previous.type === "agent" &&
        // We only care about updates
        isDelta &&
        // and only if they're text blocks
        current.content.output.case === "text" &&
        previous.content.output.case === "text"
    ) {
        return {
            ...previous,
            content: {
                ...previous.content,
                output: {
                    ...previous.content.output,
                    value: {
                        ...previous.content.output.value,
                        content: previous.content.output.value.content + current.content.output.value.content,
                    },
                },
            },
        };
    }

    // For all other cases, the current block is the new ground truth
    return current;
}
